/**
 * **打击那两层"整块画面"的叠加**：白闪、暗角/光圈收束、边缘红脉冲。
 *
 * 它们在**画布坐标**里（挂在 `app.stage` 上，不在 `world` 里）—— 因为
 * "闪光铺满整块画面"要的正是**连黑边一起**（`world` 只覆盖逻辑画布，窗口不是
 * 16:10 时四周还有一圈 `BASE.void`）。
 *
 * ## 为什么一个滤镜都不用
 *
 * `docs/dying-impact.md` §3.4：`world` 上已经挂着故障滤镜、背景/濒死层里还有
 * 各自的滤镜，而**嵌套滤镜刚刚炸过一次**（拖窗口整屏黑，见
 * `render/filterStack.ts`），软件渲染下也很贵。所以：
 *
 * | 效果 | 怎么做 |
 * | --- | --- |
 * | 白闪 | 一张 `Texture.WHITE` 的 `Sprite` 铺满画布，改 `alpha` |
 * | 暗角 / 光圈收束 | **烘一张**白→透明的径向渐变贴图（`tint` 染黑），改 `alpha` |
 * | 边缘红脉冲 | 同一张贴图，`tint = 印刷红`，改 `alpha` |
 *
 * 一张 256×256 的贴图拉满整屏足够：暗角本来就是低频的，采样放大看不出来。
 *
 * ⚠️ 颜色那三项（去色 / 色差 / 反相）**不在这里** —— 它们塞进现有那趟
 * 故障滤镜的 uniform（`GlitchFilter.setColors`），一行额外 pass 都不花。
 */

import { Container, DOMAdapter, Sprite, Texture } from 'pixi.js';
import { IMPACT_EDGE_COLOR, type ImpactChannels } from './impact';
import { BASE } from './palette';

/** 径向渐变贴图的分辨率。低频图像，256 够；大了只是白花显存 */
const VIGNETTE_PX = 256;

/**
 * 烘一张"中心透、边缘不透"的径向渐变（**白色**，靠 `tint` 染色）。
 *
 * 用 canvas 的 `createRadialGradient` 而不是画 N 个同心圆：同心圆的步进在
 * 大屏上能看出环带（每一环都是一条硬边），而暗角是低频的，环带特别显眼。
 */
function bakeVignette(): Texture {
  const size = VIGNETTE_PX;
  const canvas = DOMAdapter.get().createCanvas(size, size);
  const ctx = canvas.getContext('2d');
  if (ctx === null) return Texture.WHITE;
  const half = size / 2;
  const gradient = ctx.createRadialGradient(half, half, 0, half, half, half);
  // 中间 45% 完全不压暗，之后一路到边缘
  gradient.addColorStop(0, 'rgba(255,255,255,0)');
  gradient.addColorStop(0.45, 'rgba(255,255,255,0)');
  gradient.addColorStop(0.72, 'rgba(255,255,255,0.55)');
  gradient.addColorStop(1, 'rgba(255,255,255,1)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  return Texture.from(canvas);
}

export class ImpactLayer {
  /** 挂到 `app.stage` 上的那个容器 */
  readonly view: Container;

  readonly #flash: Sprite;
  readonly #vignette: Sprite;
  readonly #edge: Sprite;
  readonly #texture: Texture;
  #width = 0;
  #height = 0;

  constructor() {
    this.view = new Container();
    this.view.label = 'impact';
    // ⚠️ 不吃指针：它盖着整块画面，`static` 会把所有点击都吃掉
    this.view.eventMode = 'none';

    this.#texture = bakeVignette();

    // 暗角：黑（`BASE.void`）的 alpha 从中心 0 到边缘 1
    this.#vignette = new Sprite(this.#texture);
    this.#vignette.tint = BASE.void;
    this.#vignette.alpha = 0;
    this.#vignette.eventMode = 'none';
    // 边缘红脉冲：同一张贴图染红
    this.#edge = new Sprite(this.#texture);
    this.#edge.tint = IMPACT_EDGE_COLOR;
    this.#edge.alpha = 0;
    this.#edge.eventMode = 'none';
    // 白闪在最上面（它要盖住包括暗角在内的一切）
    this.#flash = new Sprite(Texture.WHITE);
    this.#flash.alpha = 0;
    this.#flash.eventMode = 'none';

    this.view.addChild(this.#vignette, this.#edge, this.#flash);
  }

  /** 画布尺寸变了（窗口/DPR）。只在真的变了时重排 */
  resize(width: number, height: number): void {
    if (width === this.#width && height === this.#height) return;
    this.#width = width;
    this.#height = height;
    // ⚠️ 暗角那张是**正方形**贴图，铺成非正方形会把它拉成椭圆 —— 所以按
    //    "能盖住整块画面"的最大边长铺，再居中（多出来的部分在屏幕外）
    const span = Math.max(width, height);
    for (const sprite of [this.#vignette, this.#edge]) {
      sprite.width = span;
      sprite.height = span;
      sprite.position.set((width - span) / 2, (height - span) / 2);
    }
    this.#flash.width = width;
    this.#flash.height = height;
    this.#flash.position.set(0, 0);
  }

  /** 把这一帧的通道写进画面 */
  render(channels: ImpactChannels): void {
    this.#flash.tint = channels.flashColor;
    this.#flash.alpha = channels.flash;
    this.#vignette.alpha = channels.vignette;
    // 边缘脉冲叠在暗角上：暗角压黑四角、脉冲把四角染红
    this.#edge.alpha = channels.edge * 0.5;
  }

  destroy(): void {
    this.view.destroy({ children: true });
    this.#texture.destroy(true);
  }
}
